import { useState, useEffect, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { FaUserMd, FaFileInvoiceDollar, FaPrint } from "react-icons/fa";
import DynamicSelect from "../DynamicSelect";
import PageHeading from "../PageHeading";
import DoctorStatementPrint from "../DoctorStatementPrint";

// API Config
const API_BASE_URL = "https://api.muktihospital.com/api";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const DoctorLedger = () => {
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ totalIncome: 0 });
  const [filterType, setFilterType] = useState("All");
  const [isPrintingStatement, setIsPrintingStatement] = useState(false);

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Accounts" },
    { label: "Doctor Ledger" },
  ];

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/doctor`, { headers: { "x-api-key": API_KEY } });
        
        let rawData = [];
        if (Array.isArray(res.data)) rawData = res.data;
        else if (res.data?.doctors) rawData = res.data.doctors;
        else if (res.data?.data) rawData = res.data.data;

        const options = rawData.map((d) => {
            let name = d.name || "Unknown";
            let spec = "";
            if(d.translations) {
                try {
                    const t = typeof d.translations === 'string' ? JSON.parse(d.translations) : d.translations;
                    name = t?.en?.name || t?.bn?.name || name;
                    spec = t?.en?.specialization || t?.bn?.specialization || "";
                } catch(e){}
            }
            return {
                value: d.id,
                label: `${name} ${spec ? `(${spec})` : ''}`,
                details: { ...d, parsedName: name, parsedSpec: spec } 
            };
        });
        setDoctors(options);
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    };
    fetchDoctors();
  }, []);

  const handleDoctorSelect = async (selectedOption) => {
    if (!selectedOption) {
      setSelectedDoctor(null);
      setLedgerData([]);
      setStats({ totalIncome: 0 });
      return;
    }

    setSelectedDoctor(selectedOption.details);
    setLoading(true);
    setFilterType("All");

    try {
      const res = await axios.get(`${API_BASE_URL}/doctor/${selectedOption.value}/ledger`, { 
        headers: { "x-api-key": API_KEY } 
      });
      setLedgerData(res.data.ledger); 
      setStats(res.data.summary);
    } catch (error) {
      console.error("Error fetching ledger:", error);
      toast.error("Failed to load doctor history.");
      setLedgerData([]); 
    } finally {
      setLoading(false);
    }
  };

  // Filter Logic
  const filteredLedgerData = useMemo(() => {
    if (filterType === "All") return ledgerData;
    return ledgerData.filter(item => item.type === filterType);
  }, [ledgerData, filterType]);

   const handlePrintStatement = () => {
    setIsPrintingStatement(true);
    setTimeout(() => {
        window.print();
        setIsPrintingStatement(false);
    }, 100);
  };


  const displayDoctor = selectedDoctor ? {
      name: selectedDoctor.parsedName,
      spec: selectedDoctor.parsedSpec,
      email: selectedDoctor.email
  } : { name: "No Doctor Selected", spec: "--", email: "--" };

  return (
    <div className={`bg-gray-50 pb-12 ${isPrintingStatement ? 'printing-statement-mode' : ''}`}>
        <style>{`
        #print-section {
          display: none;
        }

        @media print {
          .printing-statement-mode body * {
            visibility: hidden;
          }

          .printing-statement-mode #screen-section {
            display: none !important;
          }

          .printing-statement-mode #print-section {
            display: block !important;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            z-index: 9999;
            background: white;
            visibility: visible;
          }

          .printing-statement-mode #print-section * {
            visibility: visible;
          }

          .printing-statement-mode html, 
          .printing-statement-mode body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <ToastContainer />
      <PageHeading title="Doctor Ledger" breadcrumbs={breadcrumbs} />

      <div>
        {/* --- SECTION 1: SEARCH BAR --- */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200/60 p-1 mb-8">
          <div className="flex flex-col sm:flex-row items-center gap-4 p-4">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${selectedDoctor ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-400'}`}>
              <FaUserMd />
            </div>
            <div className="flex-1 w-full">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block ml-1">
                Find Doctor
              </label>
              <div className="w-full">
                <DynamicSelect
                  options={doctors}
                  onChange={handleDoctorSelect}
                  placeholder="Search by Doctor Name..."
                  isSearchable
                  isClearable
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="animate-fade-in-up">
          {/* --- SECTION 2: DOCTOR PROFILE & SUMMARY --- */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            
            {/* Profile Card */}
            <div className="lg:col-span-4 flex flex-col">
              <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-6 h-full relative overflow-hidden transition-all duration-300 ${!selectedDoctor ? 'opacity-70 grayscale' : ''}`}>
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4 relative">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold shadow-md ring-4 ring-white ${selectedDoctor ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
                            {selectedDoctor ? displayDoctor.name.charAt(0) : <FaUserMd size={24}/>}
                        </div>
                        <div>
                            <h2 className={`text-xl font-bold leading-tight ${selectedDoctor ? 'text-gray-800' : 'text-gray-400'}`}>
                            {displayDoctor.name}
                            </h2>
                            <span className="text-sm text-purple-600 font-medium">
                            {displayDoctor.spec}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="space-y-3 relative">
                  <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <span className="font-bold text-gray-400">Email:</span>
                    <span className="truncate">{displayDoctor.email}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Stats */}
            <div className="lg:col-span-8">
               <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between relative overflow-hidden h-full">
                  <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Commission / Fees</p>
                        <h3 className="text-4xl font-bold text-purple-600 mt-2">৳ {stats.totalIncome.toLocaleString()}</h3>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-full text-purple-600">
                        <FaFileInvoiceDollar size={40}/>
                    </div>
                  </div>
                  <div className="mt-6 text-sm text-gray-500">
                      Total earned from {ledgerData.length} transactions.
                  </div>
               </div>
            </div>
          </div>

          {/* --- SECTION 3: TRANSACTION TABLE --- */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center bg-gray-50/50 gap-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-6 bg-purple-600 rounded-full"></span>
                  Payment History
                </h3>
              </div>
              <button 
                onClick={handlePrintStatement}
                disabled={!selectedDoctor || ledgerData.length === 0}
                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition shadow-sm disabled:opacity-50"
              >
                <FaPrint /> Print Statement
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-100/70 text-gray-600 uppercase font-semibold text-xs border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Bill No</th>
                    <th className="px-6 py-4">Patient Name</th>
                    <th className="px-6 py-4">Type</th>
                    <th className="px-6 py-4">Note</th>
                    <th className="px-6 py-4 text-right">Credit Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-20 text-center">Loading...</td>
                    </tr>
                  ) : filteredLedgerData.length > 0 ? (
                    filteredLedgerData.map((item, index) => (
                      <tr key={index} className="hover:bg-purple-50/30 transition duration-200">
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                          {new Date(item.date).toLocaleDateString("en-GB")}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200">
                            {item.billNo}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-700">
                            {item.patientName}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                            {item.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 italic">
                            {item.note || '-'}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-green-600">
                            + ৳ {item.amount.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-16 text-center text-gray-400">
                        No transaction history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
     <div id="print-section">
        {selectedDoctor && (
          <DoctorStatementPrint
            doctor={displayDoctor}
            transactions={filteredLedgerData}
            summary={stats}
          />
        )}
      </div>
    </div>
  );
};

export default DoctorLedger;
